<template>
  <b-container fluid>
    <b-row>
      <b-col class="d-flex">
        <backlink class="mt-20" />
        <div class="flex-grow-1">
          <h1>
            Référence unique
            <div v-if="!isBusy" class="subtitle">#{{ values.internal_code }}</div>
            <b-badge variant="primary">{{ values.status }}</b-badge>
          </h1>
        </div>
        <div v-if="values.id" class="text-right">
          <b-button variant="outline-primary" size="lg" @click="duplicateItem()">
            Dupliquer la référence
            <i class="far fa-copy fa-lg ml-10"></i>
          </b-button>
          <b-button variant="outline-primary ml-10" size="lg" @click="openFormModal(values.id, 'config')">
            Modifier la fiche
            <i class="far fa-pencil fa-lg ml-10"></i>
          </b-button>
          <b-button variant="outline-primary ml-10" size="lg" v-if="!config.hideActionPdfButton"
            @click="$root.exportPdf(exportPdfData)">
            Exporter en PDF
            <i class="far fa-file-alt fa-lg ml-10"></i>
          </b-button>
          <template v-if="$root.isAuthorized('warehouse-transfer_new')">
            <router-link
              v-if="!transferDisabledReason"
              :to="{ name: 'WarehouseTransferForm', query: transferQuery }"
              class="btn btn-outline-primary btn-lg ml-10">
              Transférer vers un entrepôt
              <i class="far fa-exchange fa-lg ml-10"></i>
            </router-link>
            <span
              v-else
              v-b-tooltip.hover
              :title="transferDisabledReason"
              class="d-inline-block ml-10"
              tabindex="0">
              <b-button variant="outline-secondary" size="lg" disabled class="text-muted" style="pointer-events: none">
                Transférer vers un entrepôt
                <i class="far fa-exchange fa-lg ml-10"></i>
              </b-button>
            </span>
          </template>
          <b-button variant="primary ml-10" size="lg" class="button-create-return" v-if="rawValues.status.status_slug == 'reference-en-location'" @click="openFormBusinessReturnModal()">
            Créer un retour
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div v-if="isBusy" class="loader"></div>
    <p v-if="isBusy" class="text-center">Les données sont en cours de chargement...</p>
    <b-row v-else>
      <b-col>
        <div class="ibox">
          <div class="ibox-content divided">
            <b-row v-if="rawValues.status.status_slug == 'reference-en-location'">
              <b-col class="d-flex">
                <div class="pr-25">
                  <h3>Tarification en cours</h3>
                  <dl class="small">
                    <dt>Numéro d'affaire</dt>
                    <dd style="clear: both">{{ currentBusiness.number }}</dd>
                    <dt>Affaire</dt>
                    <dd style="clear: both">{{ currentBusiness.name }}</dd>
                    <dt>Utilisateur</dt>
                    <dd style="clear: both">
                      {{ currentEmployee ? currentEmployee.fullname : "-" }}
                    </dd>
                  </dl>
                </div>
                <PriceGrids
                  class="flex-grow-1"
                  :prices="rawValues.product ? rawValues.product.prices : []"
                  :active-range="rawValues.current_price"
                  show-single-label
                />
              </b-col>
            </b-row>
            <b-row class="main-content">
              <b-col cols="6">
                <b-row>
                  <b-col>
                    <h2>
                      <router-link v-if="rawValues.product && rawValues.product.id" :to="{
              name: 'ProductDetail',
              params: { id: rawValues.product.id },
            }">{{ values.product }}</router-link>
                    </h2>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="5" v-if="picture">
                    <img :src="picture" class="w-100" />
                  </b-col>
                  <b-col>
                    <dl class="small">
                      <dt>{{ labels.purchase_date }} :</dt>
                      <dd>{{ values.purchase_date || "-" }}</dd>
                      <dt>{{ labels.purchase_price }} :</dt>
                      <dd>{{ values.purchase_price || "-" }}</dd>
                      <dt>{{ labels.order_number }} :</dt>
                      <dd>{{ values.order_number || "-" }}</dd>
                    </dl>
                    <dl>
                      <dt>{{ labels.internal_code }} :</dt>
                      <dd>{{ values.internal_code || "-" }}</dd>
                      <dt>{{ labels.supplier }} :</dt>
                      <dd>{{ values.supplier || "-" }}</dd>
                      <dt>{{ labels.brand }} :</dt>
                      <dd>{{ values.brand || "-" }}</dd>
                      <dt>{{ labels.model }} :</dt>
                      <dd>{{ values.model || "-" }}</dd>
                      <dt>{{ labels.serial_number }} :</dt>
                      <dd>{{ values.serial_number || "-" }}</dd>
                      <dt>{{ labels.return_mode }} :</dt>
                      <dd>{{ values.return_mode || "-" }}</dd>
                      <dt>{{ labels.comment }} :</dt>
                      <dd class="nl2br">{{ values.comment || "-" }}</dd>
                    </dl>
                  </b-col>
                </b-row>
              </b-col>
              <b-col class="d-flex flex-column">
                <h3>Stockage</h3>
                <div class="storage-panel">
                  <!-- Entrepôt de rattachement de la référence et stock réel du produit -->
                  <div class="storage-current">
                    <i class="far fa-warehouse storage-current__icon"></i>
                    <div class="storage-current__body">
                      <span class="storage-label">{{ labels.current_warehouse }}</span>
                      <strong v-if="rawValues.current_warehouse" class="storage-current__name">
                        {{ rawValues.current_warehouse.name || rawValues.current_warehouse.internal_code }}
                        <span v-if="rawValues.current_warehouse.name" class="storage-code">{{ rawValues.current_warehouse.internal_code }}</span>
                      </strong>
                      <span v-else class="text-muted">Non affecté</span>
                      <small v-if="rawValues.current_warehouse && !isInStock" class="storage-current__hint">
                        {{ rawValues.status ? rawValues.status.name : "" }} — pas physiquement dans l'entrepôt
                      </small>
                    </div>
                    <b-badge class="storage-current__stock" :variant="currentStock <= 0
                      ? 'danger'
                      : currentStock <= rawValues.product.stock_mini
                        ? 'warning'
                        : 'success'
                      ">Stock réel : {{ currentStock }}</b-badge>
                  </div>

                  <!-- Environnements de stockage du produit, un par entrepôt (lecture seule) -->
                  <span class="storage-label">Environnements de stockage</span>
                  <ul v-if="sortedProductStorages.length" class="storage-list">
                    <li v-for="storage in sortedProductStorages" :key="storage.warehouse_id"
                      :class="['storage-item', { 'storage-item--current': isCurrentWarehouse(storage) }]">
                      <div class="storage-item__warehouse">
                        <span>{{ storage.warehouse_name }}</span>
                        <span class="storage-code">{{ storage.warehouse_code }}</span>
                        <span v-if="isCurrentWarehouse(storage)" class="storage-item__here">
                          <i class="far fa-check"></i> Entrepôt actuel de cette référence
                        </span>
                      </div>
                      <div class="storage-item__path">
                        <i class="far fa-map-marker-alt"></i>
                        <span class="storage-item__zone">{{ storage.storage_zone_name }}</span>
                        <i class="far fa-chevron-right storage-item__sep"></i>
                        <router-link v-if="$root.isAuthorized('storage-environment_show')" class="storage-item__env" :to="{
                          name: 'StorageEnvironmentDetail',
                          params: { id: storage.storage_environment_id },
                        }">{{ storage.storage_environment_name }}</router-link>
                        <span v-else class="storage-item__env">{{ storage.storage_environment_name }}</span>
                      </div>
                    </li>
                  </ul>
                  <p v-else class="storage-empty">
                    <i class="far fa-info-circle"></i>
                    Aucun emplacement défini pour ce produit. Il se renseigne depuis la fiche produit,
                    dans la répartition du stock par entrepôt.
                  </p>
                </div>
              </b-col>
            </b-row>
            <!-- QR-002 : le QR code complète le code barre, il ne le remplace pas.
                 Les deux formats cohabitent durablement (tout le parc en location
                 revient sans QR pendant des années). Les deux étiquettes partagent
                 une ligne dédiée : à l'étroit dans la ligne principale, ni le code
                 barre ni le QR n'étaient lisibles à l'écran. -->
            <b-row class="codes-row">
              <b-col class="d-flex flex-column barcode-col">
                <h3>Code barre</h3>
                <b-row class="flex-grow-1 flex-column barcode-wrapper justify-content">
                  <div class="text-center">
                    <div>{{ values.internal_code }}</div>
                  </div>
                  <div class="text-center">
                    <div>{{ values.product }}</div>
                  </div>
                  <barcode class="text-center" :value="values.internal_code" :displayValue="false" height="50">
                    Impossible
                    de générer le code à barres</barcode>
                </b-row>
                <b-row>
                  <b-col class="text-center bottom-link">
                    <a href @click.prevent="printBarcode()">
                      Imprimer l'étiquette
                      <i class="fa fa-chevron-right"></i>
                    </a>
                  </b-col>
                </b-row>
              </b-col>
              <b-col class="d-flex flex-column qrcode-col">
                <h3>QR code</h3>
                <b-row class="flex-grow-1 flex-column qrcode-wrapper justify-content">
                  <div class="text-center">
                    <div>{{ values.internal_code }}</div>
                  </div>
                  <div class="text-center qrcode-svg" v-if="qrCodeSvg" v-html="qrCodeSvg"></div>
                  <div class="text-center" v-else-if="qrCodeError">
                    Impossible de générer le QR code
                  </div>
                  <div class="text-center" v-else>Génération…</div>
                </b-row>
                <b-row>
                  <b-col class="text-center bottom-link">
                    <a href @click.prevent="printQrCode()" v-if="qrCodeSvg">
                      Imprimer l'étiquette
                      <i class="fa fa-chevron-right"></i>
                    </a>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="auto" class="indicator flex-fill">
                <div class="flex-grow-1">
                  <dl>
                    <dd>Total en jour de location</dd>
                    <dt>depuis la date d'achat</dt>
                  </dl>
                </div>
                <h2>{{ values.total_rental_days }}</h2>
              </b-col>
              <b-col cols="auto" class="indicator flex-fill">
                <div class="flex-grow-1">
                  <dl>
                    <dd>Montant total de location</dd>
                    <dt>depuis la date d'achat</dt>
                  </dl>
                </div>
                <h2>{{ values.total_rental_amount }}</h2>
              </b-col>
              <b-col cols="auto" class="indicator flex-fill">
                <div class="flex-grow-1">
                  <dl>
                    <dd>Coût total</dd>
                    <dt>du matériel</dt>
                  </dl>
                </div>
                <h2>{{ values.reference_total_cost }}</h2>
              </b-col>
              <b-col cols="auto" class="indicator flex-fill">
                <div class="flex-grow-1">
                  <dl>
                    <dd>Marge</dd>
                    <dt>brute</dt>
                  </dl>
                </div>
                <h2>
                  {{
              (
                parseFloat(rawValues.total_rental_amount) -
                parseFloat(rawValues.reference_total_cost)
              ).toFixed(2)
            }}€
                </h2>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="!isBusy">
      <b-col v-if="rawValues.reference_logs && rawValues.reference_logs.length">
        <div class="ibox">
          <div class="ibox-title">
            <i class="far fa-wrench"></i> Historique des mouvements
            <a v-if="referenceLogsItems.length > 5" href @click.prevent="toggleRefLogsList($event)"
              class="deploy-all">{{ isActiveRefLogsItems ? "Voir tout" : "Réduire" }}</a>
          </div>
          <div class="ibox-content">
            <div class="table-wrapper">
              <b-table hover :fields="reference_log_fields"
                :items="activeRefLogsItems != null ? activeRefLogsItems : referenceLogsItems.slice(0, 5)"
                foot-clone class="ref-logs-table">
                <template slot="agency" slot-scope="data">
                  <strong>
                    <router-link
                      v-if="data.value && referenceLogsById[data.item.id] && referenceLogsById[data.item.id].agency.id"
                      :to="{ name: 'AgencyDetail', params: { id: referenceLogsById[data.item.id].agency.id } }"
                      target="_blank"
                      >{{ data.value }}</router-link
                    >
                    <template v-else>{{ data.value }}</template>
                  </strong>
                </template>
                <template slot="business" slot-scope="data">
                  <router-link
                    v-if="data.value && referenceLogsById[data.item.id] && referenceLogsById[data.item.id].business.id"
                    :to="{ name: 'BusinessDetail', params: { id: referenceLogsById[data.item.id].business.id } }"
                    target="_blank"
                    >{{ data.value }}</router-link
                  >
                  <template v-else>{{ data.value }}</template>
                </template>
                <template slot="context" slot-scope="data">
                  <b-badge variant="primary">{{ data.value }}</b-badge>
                </template>
                <template slot="duration" slot-scope="data">
                  <b-badge variant="info">{{ data.value }}</b-badge>
                </template>
                <template slot="end_date" slot-scope="data">
                  {{ data.value }}
                  <small v-if="data.item.billing_end_date && data.item.billing_end_date !== data.value"
                    class="d-block text-muted">
                    Facturation arrêtée au {{ data.item.billing_end_date }}
                  </small>
                </template>
                <!-- Ligne de total : tous les mouvements, même masqués -->
                <template slot="FOOT_agency" slot-scope="data">
                  Total{{ isActiveRefLogsItems && referenceLogsItems.length > 5 ? " (tous les mouvements)" : "" }}
                </template>
                <template slot="FOOT_business" slot-scope="data">&nbsp;</template>
                <template slot="FOOT_start_date" slot-scope="data">&nbsp;</template>
                <template slot="FOOT_end_date" slot-scope="data">&nbsp;</template>
                <template slot="FOOT_context" slot-scope="data">&nbsp;</template>
                <template slot="FOOT_duration" slot-scope="data">
                  <b-badge variant="info">{{ referenceLogsTotals.duration }}</b-badge>
                </template>
                <template slot="FOOT_renting_total_price" slot-scope="data">
                  {{ referenceLogsTotals.price.toFixed(2) }}€
                </template>
              </b-table>
            </div>
            <a v-if="isActiveRefLogsItems && referenceLogsItems.length > 5" href
              @click.prevent="toggleRefLogsList($event)" class="ref-logs-more">
              + {{ referenceLogsItems.length - 5 }} {{ referenceLogsItems.length - 5 > 1 ? "lignes masquées" : "ligne masquée" }} — tout afficher
            </a>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="!isBusy">
      <b-col>
        <div class="ibox">
          <div class="ibox-title">
            <i class="far fa-history"></i>Historique de demandes
            <a v-if="rawValues.demands && rawValues.demands.length > 5" href @click.prevent="toggleDemandsList($event)"
              class="deploy-all">Voir
              tout</a>
          </div>
          <div class="ibox-content">
            <div class="table-wrapper">
              <b-table v-if="rawValues.demands && rawValues.demands.length" class="mt-25" hover
                :fields="historic_fields"
                :items="activeDemandsItems != null ? activeDemandsItems : rawValues.demands.slice(0, 5)"
                sort-by="start_date" :sort-desc="true">
                <template slot="type" slot-scope="data">{{
              data.value == "renting"
                ? "Location"
                : data.value == "business_return"
                  ? "Retour"
                  : data.value == "warehouse_transfer"
                    ? "Transfert inter-entrepôts"
                    : "Transfert"
            }}</template>
                <template slot="number" slot-scope="data">
                  <router-link v-if="data.item.id" :to="{
              name:
                data.item.type == 'renting'
                  ? 'RentingDetail'
                  : data.item.type == 'business_return'
                    ? 'BusinessReturnDetail'
                    : data.item.type == 'warehouse_transfer'
                      ? 'WarehouseTransferDetail'
                      : 'TransferDetail',
              params: { id: data.item.id },
            }">{{ data.value }}</router-link>
                </template>
                <template slot="date" slot-scope="data">{{
              data.value | moment("DD-MM-YYYY")
            }}</template>
                <template slot="status" slot-scope="data">{{
                $store.getters["status/getItem"](data.value.slug).name
              }}</template>
                <template slot="pdf" slot-scope="data">
                  <b-button v-b-tooltip.hover title="Exporter en PDF" variant="outline-secondary"
                    v-if="data.item.id && !['transfer', 'warehouse_transfer'].includes(data.item.type)" @click.prevent="exportPdfSingle(data.item)">
                    <i class="far fa-file-alt"></i>
                  </b-button>
                </template>
              </b-table>
              <p v-else class="text-left text-muted mt-15 mb-0 pl-4">
                Aucune demande pour cette référence.
              </p>
            </div>
          </div>
        </div>
      </b-col>
      <b-col>
        <div class="ibox">
          <div class="ibox-title"><i class="far fa-wrench"></i> Historique de maintenance</div>
          <div class="ibox-content">
            <b-row>
              <b-col>
                <dl>
                  <dt>Première rev.</dt>
                  <dd>{{ values.first_maintenance_date || "-" }}</dd>
                </dl>
              </b-col>
              <b-col>
                <dl>
                  <dt>Dernière rev.</dt>
                  <dd>{{ values.last_maintenance_date || "-" }}</dd>
                </dl>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <dl class="boxed">
                  <dt>Prochaine révision</dt>
                  <dd v-if="values.next_espected_maintenance_date">
                    {{ values.next_espected_maintenance_date | moment("DD-MM-YYYY") }}
                  </dd>
                  <dd v-else>Aucune</dd>
                  <i class="icone far fa-calendar-alt fa-lg"></i>
                </dl>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-button class="btn-new-maintenance" variant="outline-primary"
                  @click="openFormModal(null, 'external_maintenance_config')">
                  Enregistrer une maintenance
                  <i class="far fa-wrench"></i>
                </b-button>
              </b-col>
            </b-row>
            <div class="table-wrapper">
              <b-table v-if="maintenancesItems && maintenancesItems.length" class="mt-25" hover
                :fields="maintenance_fields" :items="maintenancesItems" :sort-desc="true">
                <template slot="maintenance_number" slot-scope="data">
                  <router-link v-if="data.item.id" :to="{
              name: 'ExternalMaintenanceDetail',
              params: { id: data.item.id },
            }">{{ data.value }}</router-link>
                </template>
                <template slot="dates" slot-scope="data">
                  {{ data.item.start_date + " / " + (data.item.end_date || " - ") }}
                </template>
                <template slot="cost" slot-scope="data">{{
                data.value ? parseFloat(data.value).toFixed(2) + "€" : null
              }}</template>
                <template slot="type" slot-scope="data">
                  {{ data.value == 'external' ? 'Externe' : 'Interne' }}
                </template>
                <template slot="duree" slot-scope="data">
                  <b-badge variant="info">{{ data.value }}</b-badge>
                </template>
                <template slot="storedFiles" slot-scope="data">
                  <div v-for="doc in data.value" :key="doc.id">
                    <a :href="backUrl + doc.file" target="_blank" class="btn btn-primary btn-xs"
                      v-if="doc.type_slug == 'external-maintenance-certificate'"><i class="far fa-download"></i></a>
                  </div>
                </template>
                <template slot="actions" slot-scope="data">
                  <b-button-group size="sm" class="actions">
                    <b-button v-b-tooltip.hover title="Supprimer" variant="outline-secondary" v-if="!external_maintenance_config.hideActionDeleteButton &&
              data.item.id &&
              ((external_maintenance_config.deleteCondition &&
                external_maintenance_config.deleteCondition(data.item)) ||
                !external_maintenance_config.deleteCondition) &&
              external_maintenance_config.authFeature &&
              $store.getters['auth/isAuthorized'](
                external_maintenance_config.authFeature + '_delete'
              )
              " @click.prevent="deleteExternalMaintenance(data.item.id)">
                      <i class="far fa-trash-alt"></i>
                    </b-button>
                  </b-button-group>
                </template>
              </b-table>
              <div class="d-flex maintenances-all-links">
                <router-link v-if="this.display_maintenance_links == true" class="link" :to="{
              name: 'ExternalMaintenanceListByFilter',
              params: { 'defaultFilterField': 'reference', 'defaultFilterValue': values.id },
            }">Voir toutes les maintenances externes</router-link>
              </div>

            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- QR-005 : la fiche imprime son étiquette QR via le même PDF serveur que la
         liste, pour que le gabarit ne dépende pas du navigateur du poste. -->
    <ReferenceLabelsModal :selected-rows="labelsSelectedRows" :allow-filter-scope="false" />

    <AdminModalBusinessReturnForm :config="business_return_config" :id="'admin_modal_business_return_form'" ref="admin_modal_business_return_form"/>
    <AdminModalForm v-if="config.useNewModal || config.useEditModal" :config="modal_config" :id_to_edit="id_to_edit"
      :params="{ reference_id: values.id }" @submit:create:success="submitCreateSuccess" :id="'admin_modal_form'"/>
  </b-container>
</template>

<script>
import config from "./config";
import reference_log_config from "@/components/ReferenceLog/config";
import external_maintenance_config from "@/components/ExternalMaintenance/config";
import renting_config from "@/components/Renting/config";
import business_return_config from "@/components/BusinessReturnMobile/config";
import transfer_config from "@/components/Transfer/config";
import AdminModalForm from "@/components/Global/AdminModalForm";
import AdminModalBusinessReturnForm from "@/components/Global/AdminModalForm";
import PriceGrids from "@/components/Global/PriceGrids";
import ReferenceLabelsModal from "./ReferenceLabelsModal";
import barcodeLabel from "@/mixins/barcodeLabel";

export default {
  name: "ReferenceDetail",
  mixins: [barcodeLabel],
  components: {
    AdminModalForm,
    AdminModalBusinessReturnForm,
    PriceGrids,
    ReferenceLabelsModal
  },
  data() {
    return {
      config: config,
      display_maintenance_links: false,
      backUrl: process.env.BACK_FILES_URL,
      reference_log_config: reference_log_config,
      external_maintenance_config: external_maintenance_config,
      renting_config: renting_config,
      business_return_config: {...business_return_config, listRouteName : 'BusinessReturnList'},
      transfer_config: transfer_config,
      modal_config: {},
      // QR-002
      qrCodeSvg: null,
      qrCodeError: false,
      labels: {},
      product_labels: {},
      reference_log_fields: [],
      activeRefLogsItems: null,
      activeDemandsItems: null,
      isActiveRefLogsItems: true,
      isActiveDemandsItem: true,
      maintenance_fields: [
        { key: "maintenance_number", label: "Numéro de maintenance" },
        { key: "dates", label: "date du contrôle" },
        { key: "cost", label: "Coût" },
        { key: "type", label: "Type" },
        { key: "duree", label: "Durée en jours" },
        { key: "storedFiles", label: "Certificat de contrôle obligatoire" },
        { key: "actions", label: "" },
      ],
      historic_fields: [
        { key: "type", label: "Type" },
        { key: "number", label: "Numéro" },
        { key: "date", label: "Date" },
        { key: "status", label: "Statut" },
        { key: "pdf", label: "PDF" },
      ],
      isBusy: false,
      id_to_edit: null,
    };
  },
  computed: {
    productStorages: function () {
      return this.rawValues.product_storages || [];
    },
    // L'entrepôt de la référence d'abord : c'est là qu'on la cherche.
    sortedProductStorages: function () {
      return [...this.productStorages].sort(
        (a, b) => this.isCurrentWarehouse(b) - this.isCurrentWarehouse(a) || String(a.warehouse_name).localeCompare(b.warehouse_name)
      );
    },
    isInStock: function () {
      return !this.rawValues.status || this.rawValues.status.status_slug == "reference-en-stock";
    },
    rawValues: function () {
      return (
        this.$store.getters[config.storeNamespace + "/getItem"](this.$route.params.id) || {
          product: {},
          status: {},
        }
      );
    },
    values: function () {
      return this.$root.formatData(
        this.rawValues,
        this.config.fields.filter((field) => field.inDetail == true)
      );
    },
    // QR-005 : la modale d'impression attend les mêmes champs qu'une ligne de la
    // liste (id, code interne, produit).
    labelsSelectedRows: function () {
      if (!this.rawValues.id) {
        return [];
      }
      return [
        {
          id: this.rawValues.id,
          internal_code: this.rawValues.internal_code,
          product: this.rawValues.product ? this.rawValues.product.name : null,
        },
      ];
    },
    currentBusiness: function () {
      return this.rawValues.reference_logs && this.rawValues.reference_logs.length
        ? this.rawValues.reference_logs.find(log => !log.end_date).business
        : {};
    },
    currentEmployee: function () {
      return this.rawValues.reference_logs && this.rawValues.reference_logs.length
        ? this.rawValues.reference_logs.find(log => !log.end_date).employee
        : {};
    },
    // Raison rendant le transfert inter-entrepôts indisponible (null si possible).
    // Seules les références en stock peuvent être transférées vers un entrepôt ;
    // sert à griser le bouton et à afficher le motif en tooltip.
    transferDisabledReason: function () {
      const status = this.rawValues.status || {};
      if (status.status_slug === "reference-en-stock") return null;
      if (status.status_slug === "reference-en-location") {
        return "Référence en location : effectuez d'abord un retour avant de pouvoir la transférer.";
      }
      const label = status.name ? `« ${status.name} »` : "actuel";
      return `Transfert impossible : seules les références en stock peuvent être transférées (statut ${label}).`;
    },
    // Query de préremplissage du transfert inter-entrepôts depuis la fiche référence.
    // La source est connue (entrepôt courant de la référence).
    transferQuery: function () {
      const query = {
        reference_id: this.rawValues.id,
        internal_code: this.rawValues.internal_code,
      };
      if (this.rawValues.current_warehouse && this.rawValues.current_warehouse.id) {
        query.source_warehouse_id = this.rawValues.current_warehouse.id;
      }
      return query;
    },
    // Dépôt où se trouve le matériel, nom complet + code (« EQUANS SM CARQUEFOU (LG8) »),
    // comme la répartition du stock de la fiche produit. La règle « seulement en stock »
    // est déjà portée par le formatter du champ (config), d'où le passage par `values`.
    currentWarehouseLabel: function () {
      if (!this.values.current_warehouse) {
        return null;
      }
      const warehouse = this.rawValues.current_warehouse;

      return warehouse.name ? warehouse.name + " (" + warehouse.internal_code + ")" : warehouse.internal_code;
    },
    currentStock: function () {
      if (this.rawValues.product.unique_references) {
        let product_references = [...this.rawValues.product.unique_references, ...this.rawValues];
        return product_references.filter((item) => item.status.status_slug == "reference-en-stock")
          .length;
      }
    },
    picture() {
      return this.rawValues.product.picture
        ? process.env.BACK_FILES_URL + this.rawValues.product.picture
        : null;
    },
    // Logs bruts par id : les lignes formatées de l'historique n'ont plus les ids
    // de l'agence et de l'affaire, nécessaires aux liens.
    referenceLogsById: function () {
      const byId = {};
      (this.rawValues.reference_logs || []).forEach((log) => {
        byId[log.id] = log;
      });
      return byId;
    },
    // Mouvements déjà triés par le back du plus récent au plus ancien (date de début) : l'id ne
    // suit pas la chronologie depuis la reprise de l'historique MCS, on ne retrie pas dessus.
    // Totaux de l'historique (tous les mouvements, même masqués) : mêmes données que le total
    // de jours et le montant de location affichés en haut de la fiche.
    referenceLogsTotals: function () {
      return (this.rawValues.reference_logs || []).reduce(
        (totals, log) => ({
          duration: totals.duration + (parseInt(log.duration, 10) || 0),
          price: totals.price + (parseFloat(log.renting_total_price) || 0),
        }),
        { duration: 0, price: 0 }
      );
    },
    referenceLogsItems: function () {
      return this.rawValues.reference_logs
        ? this.$root.formatDatas(
          this.rawValues.reference_logs,
          this.reference_log_config.fields.filter((field) => field.inDetail == true)
        )
        : [];
    },
    maintenancesItems: function () {
      let maintenances = [];
      // if (this.rawValues.internal_maintenances) {
      //   this.rawValues.internal_maintenances.forEach((internal_maintenance) => {
      //     if(maintenances.length < 4){
      //       maintenances.push({
      //         id: internal_maintenance.id,
      //         type: "internal",
      //         maintenance_number: internal_maintenance.maintenance_number,
      //         status: internal_maintenance.status.status_slug,
      //         start_date: internal_maintenance.intervention_start_date
      //           ? this.$moment(internal_maintenance.intervention_start_date).format("DD-MM-YYYY")
      //           : null,
      //         end_date: internal_maintenance.intervention_end_date
      //           ? this.$moment(internal_maintenance.intervention_end_date).format("DD-MM-YYYY")
      //           : null,
      //         cost: null,
      //         duree: internal_maintenance.intervention_start_date
      //           ? internal_maintenance.intervention_end_date
      //             ? this.$moment(internal_maintenance.intervention_end_date).diff(
      //               this.$moment(internal_maintenance.intervention_start_date),
      //               "days"
      //             )
      //             : this.$moment().diff(
      //               this.$moment(internal_maintenance.intervention_start_date),
      //               "days"
      //             )
      //           : null,
      //       });
      //     }else{
      //       this.display_maintenance_links = true
      //     }

      //   });
      // }
      if (this.rawValues.external_maintenances) {
        this.rawValues.external_maintenances.forEach((external_maintenance) => {
          if(maintenances.length < 4){
            maintenances.push({
              id: external_maintenance.id,
              type: "external",
              maintenance_number: external_maintenance.maintenance_number,
              status: null,
              start_date: external_maintenance.start_date
                ? this.$moment(external_maintenance.start_date).format("DD-MM-YYYY")
                : null,
              end_date: external_maintenance.end_date
                ? this.$moment(external_maintenance.end_date).format("DD-MM-YYYY")
                : null,
              cost: external_maintenance.price || null,
              storedFiles: external_maintenance.storedFiles,
              duree: external_maintenance.start_date
                ? external_maintenance.end_date
                  ? this.$moment(external_maintenance.end_date).diff(
                    this.$moment(external_maintenance.start_date),
                    "days"
                  )
                  : this.$moment().diff(this.$moment(external_maintenance.start_date), "days")
                : null,
            });
          }else{
            this.display_maintenance_links = true
          }
        });
      }
      return maintenances;
    },
    exportPdfData: function () {
      let exportPdfData = {
        title: this.config.pdf_export_title
          ? this.config.pdf_export_title(this.rawValues, this.values, this)
          : this.values[this.config.titleField],
        link: this.config.pdf_export_link
          ? this.config.pdf_export_link(this.rawValues, this.values, this)
          : null,
        infos: [],
      };

      if (this.config.pdf_export_logo) {
        exportPdfData.logo = this.config.pdf_export_logo;
      }

      if (this.config.pdf_export_extra_content_before) {
        exportPdfData.extra_content_before = this.config.pdf_export_extra_content_before(
          this.rawValues,
          this.values,
          this
        );
      }

      if (this.config.pdf_export_custom_main_content) {
        exportPdfData.custom_main_content = this.config.pdf_export_custom_main_content(
          this.rawValues,
          this.values,
          this
        );
      } else {
        this.config.fields
          .filter((field) => field.inPdfExport == true)
          .forEach((field) => {
            exportPdfData.infos.push([
              field.label + " :",
              field.type == "yesno"
                ? this.values[field.name] == true
                  ? "Oui"
                  : "Non"
                : this.values[field.name] || null,
            ]);
          });
      }

      exportPdfData.lists = [];
      if (this.lists && this.lists.length) {
        this.lists.forEach((list) => {
          let currentList = {
            label: list.label,
            infos: [this.lists_fields[list.name].map((f) => f.label)],
          };
          this.listsItems[list.name].forEach((item) => {
            let infosItem = [];
            list.fields.forEach((field) => {
              infosItem.push(item[field] || null);
            });
            currentList.infos.push(infosItem);
          });
          exportPdfData.lists.push(currentList);
        });
      }

      if (this.config.pdf_export_extra_lists) {
        exportPdfData.lists.push(
          ...this.config.pdf_export_extra_lists(this.rawValues, this.values, this)
        );
      }

      if (this.config.pdf_export_extra_content_after) {
        exportPdfData.extra_content_after = this.config.pdf_export_extra_content_after(
          this.rawValues,
          this.values,
          this
        );
      }

      return exportPdfData;
    },
  },
  watch: {
    // Cas parcitulier pour le passage d'une ref à l'autre depuis le champ de recherche général
    "$route.params.id"(newId) {
      this.$store.dispatch(config.storeNamespace + "/getItem", {
        id: newId,
      });
      this.loadQrCode(newId);
    },
  },
  mounted() {
    // Chargement des champs à afficher dans le détail depuis la config
    // Et récupération des libellés
    config.fields
      .filter((field) => field.inDetail == true)
      .forEach((field) => {
        this.labels[field.name] = field.label;
      });

    this.isBusy = true;
    this.$store
      .dispatch(config.storeNamespace + "/getItem", {
        id: this.$route.params.id,
        wait: true,
      })
      .then(() => {
        // Test si le contenu demandé est bien trouvé
        let self = this;
        if (Object.entries(this.rawValues).length <= 2) {
          this.$toastr.error(
            "La fiche demandée n'a pas été trouvée. Assure-vous que l'URL saisie soit correcte."
          );
          setTimeout(function () {
            self.$router.go(-1);
          }, 5000);
          return;
        }
        this.isBusy = false;
      });

    // Préparation des champs pour les listes des logs
    let reference_log_fields = reference_log_config.fields
      .filter((field) =>
        [
          "id",
          "agency",
          "business",
          "context",
          "start_date",
          "end_date",
          "duration",
          "renting_total_price",
        ].includes(field.name)
      )
      .map((field) => ({
        key: field.name,
        label: field.label,
      }));
    this.reference_log_fields = reference_log_fields;

    this.loadQrCode(this.$route.params.id);
  },
  methods: {
    // QR-002
    loadQrCode(id) {
      this.qrCodeSvg = null;
      this.qrCodeError = false;
      this.$store
        .dispatch(config.storeNamespace + "/getQrCode", id)
        .then((svg) => {
          this.qrCodeSvg = svg;
        })
        .catch(() => {
          this.qrCodeError = true;
        });
    },
    openFormModal(id, config) {
      this.id_to_edit = id;
      this.modal_config = this[config];
      this.$root.$emit("bv::show::modal", "admin_modal_form");
    },
    openFormBusinessReturnModal() {
      this.$root.$emit("bv::show::modal", "admin_modal_business_return_form");
    },
    toggleRefLogsList(e){
      if(!this.isActiveRefLogsItems){
        this.activeRefLogsItems = this.referenceLogsItems.slice(0, 5);
        this.isActiveRefLogsItems = true;
      }else{
        this.activeRefLogsItems = this.referenceLogsItems;
        this.isActiveRefLogsItems = false
      }
    },
    toggleDemandsList(e){
      if(!this.isActiveDemandsItem){
        this.activeDemandsItems = this.rawValues.demands.slice(0, 5);
        this.isActiveDemandsItem = true;
      }else{
        this.activeDemandsItems = this.rawValues.demands;
        this.isActiveDemandsItem = false;
      }
    },
    /**
     * Change le statut de la demande pour 'En attente de traitement'
     */
    duplicateItem() {
      // Création d'une copie en retirant les champs uniques et en récupérant les id des relations
      let copy = {
        ...this.rawValues,
        product_id: this.rawValues.product ? this.rawValues.product.id : null,
        current_warehouse_id: this.rawValues.current_warehouse ? this.rawValues.current_warehouse.id : null,
        supplier_id: this.rawValues.supplier ? this.rawValues.supplier.id : null,
        status_slug: "reference-en-stock",
      };
      copy.id = null;
      copy.first_maintenance_date = null;
      copy.last_maintenance_date = null;

      // On stoque la copie dans le store pour la récupérer ensuite dans le formulaire
      this.$store.commit("reference/prepareCopy", copy);

      // Ouverture du formulaire de création qui sera pré-rempli
      this.openFormModal(null, "config");
    },
    isCurrentWarehouse(storage) {
      return Boolean(this.rawValues.current_warehouse && this.rawValues.current_warehouse.id == storage.warehouse_id);
    },
    printBarcode() {
      this.printBarcodeLabel();
    },
    /**
     * QR-005 : l'étiquette QR passe par le PDF serveur (même gabarit que
     * l'action groupée de la liste), et non plus par window.print() dont le
     * rendu variait d'un navigateur à l'autre.
     */
    printQrCode() {
      this.$root.$emit("bv::show::modal", "modal-reference-labels");
    },
    submitCreateSuccess(response) {
      // Si il s'agit du formulaire de création d'une ref, c'est qu'on vient de valider une duplication
      if (response.config.url.indexOf("/reference")) {
        this.$router.push({
          name: "ReferenceDetail",
          params: { id: response.data.object.id },
        });
      }
    },
    /**
     * Exporte en PDF une seule ligne (bouton sur la ligne)
     * Prépare les données pour les envoyer à la fonction globale exportPdf()
     * @param {integer} item  Item à exporter
     */
    exportPdfSingle(item) {
      var config;
      switch (item.type) {
        case "renting":
          config = this.renting_config;
          break;
        case "business_return":
          config = this.business_return_config;
          break;
        case "transfer":
          config = this.transfer_config;
          break;
      }

      this.$store
        .dispatch(config.storeNamespace + "/getItem", {
          id: item.id,
          wait: true,
        })
        .then(() => {
          item = this.$store.getters[config.storeNamespace + "/getItem"](item.id);

          let formattedItem = this.$root.formatData(
            item,
            config.fields.filter((field) => field.inPdfExport == true)
          );

          let exportPdfData = {
            title: config.pdf_export_title
              ? config.pdf_export_title(item, formattedItem, this)
              : item[config.titleField],
            link: config.pdf_export_link ? config.pdf_export_link(item, formattedItem, this) : null,
            infos: [],
          };

          if (config.pdf_export_logo) {
            exportPdfData.logo = config.pdf_export_logo;
          }

          if (config.pdf_export_extra_content_before) {
            exportPdfData.extra_content_before = config.pdf_export_extra_content_before(
              item,
              formattedItem,
              this
            );
          }

          if (config.pdf_export_custom_main_content) {
            exportPdfData.custom_main_content = config.pdf_export_custom_main_content(
              item,
              formattedItem,
              this
            );
          } else {
            config.fields
              .filter((field) => field.inPdfExport == true)
              .forEach((field) => {
                exportPdfData.infos.push([
                  field.label + " :",
                  field.type == "yesno"
                    ? formattedItem[field.name] == true
                      ? "Oui"
                      : "Non"
                    : formattedItem[field.name] || null,
                ]);
              });
          }

          let lists = config.fields.filter((field) => field.type == "list");
          exportPdfData.lists = [];
          if (lists && lists.length) {
            lists.forEach((list) => {
              let currentList = {
                label: list.label,
                infos: [
                  list.fields.map((field) => list.config.fields.find((f) => f.name == field).label),
                ],
              };

              if (item[list.name]) {
                this.$root
                  .formatDatas(
                    item[list.name],
                    list.config.fields.filter((field) => field.inDetail == true)
                  )
                  .forEach((item) => {
                    let infosItem = [];
                    list.fields.forEach((field) => {
                      infosItem.push(item[field] || null);
                    });
                    currentList.infos.push(infosItem);
                  });
              }

              exportPdfData.lists.push(currentList);
            });
          }

          if (config.pdf_export_extra_lists) {
            exportPdfData.lists.push(...config.pdf_export_extra_lists(item, formattedItem, this));
          }

          if (config.pdf_export_extra_content_after) {
            exportPdfData.extra_content_after = config.pdf_export_extra_content_after(
              item,
              formattedItem,
              this
            );
          }

          this.$root.exportPdf(exportPdfData);
        });
    },
    /**
     * Supprime une maintenance externe (via le store)
     * @param {integer} id  Id de l'item à supprimer
     */
    deleteExternalMaintenance(id) {
      if (confirm("Êtes-vous sur de vouloir supprimer cet élément ?")) {
        this.$store
          .dispatch(this.external_maintenance_config.storeNamespace + "/deleteItem", id)
          .then((response) => {
            this.$store.dispatch(config.storeNamespace + "/getItem", {
              id: this.rawValues.id,
            });
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

.ref-logs-more {
  display: block;
  padding: 8px 12px;
  text-align: center;
  color: $primary;
  font-weight: 600;
  border-top: 1px dashed #e7eaec;

  &:hover {
    color: $primary-darker;
    background: #f7f9fa;
    text-decoration: none;
  }
}


// Bloc Stockage : entrepôt de la référence, puis emplacement du produit dans chaque entrepôt.
.storage-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 12px;
}

.storage-label {
  display: block;
  color: $text-50p;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
}

.storage-code {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  background-color: $bg-1;
  color: $text-50p;
  font-size: 9px;
  letter-spacing: 0.05rem;
  vertical-align: middle;
}

.storage-current {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background-color: $bg-2;

  &__icon {
    flex: 0 0 36px;
    height: 36px;
    line-height: 36px;
    background-color: #fff;
    color: $primary;
    text-align: center;
    font-size: 14px;
  }

  &__body {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__name {
    display: block;
    font-size: 13px;
  }

  &__hint {
    display: block;
    color: $text-50p;
  }

  &__stock {
    flex: 0 0 auto;
  }
}

.storage-list {
  margin: 0 !important;
  padding: 0;
  list-style: none;
}

.storage-item {
  margin-bottom: 8px !important;
  padding: 12px 16px;
  border: solid 1px $border-10p;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:hover {
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.05);
  }

  &--current {
    background-color: rgba(26, 174, 124, 0.04);
  }

  &__warehouse {
    color: $text-50p;
    font-size: 10px;
    margin-bottom: 4px;
  }

  &__here {
    margin-left: 6px;
    color: $primary;
    font-size: 9px;
    letter-spacing: 0.05rem;

    i {
      margin-right: 2px;
    }
  }

  &__path {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 13px;

    > .fa-map-marker-alt {
      color: $primary;
    }
  }

  // Les listes des ibox sont en gras par défaut : la zone repasse en graisse normale pour
  // laisser l'environnement, là où l'on range, ressortir.
  &__zone,
  &__warehouse {
    font-family: "ClanPro" !important;
  }

  &__sep {
    color: $text-30p;
    font-size: 9px;
  }

  &__env {
    font-family: "ClanProBold" !important;
  }
}

.storage-empty {
  margin: 0;
  padding: 12px 16px;
  border: dashed 1px $border-10p;
  color: $text-50p;

  i {
    margin-right: 6px;
  }
}

.btn-new-maintenance {
  width: 100%;
  position: relative;

  i {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.maintenances-all-links {
  justify-content: space-between;
  padding: 0 20px;

  a {
    text-decoration: underline;
    color: $text-50p;
  }
}

.ibox {
  .ibox-title {
    .deploy-all {
      position: absolute;
      right: 20px;
      font-size: 10px;
      color: black;
      text-decoration: underline;
      text-transform: none;
      font-family: 'ClanPro';
      letter-spacing: normal;
    }
  }
}
</style>

<style lang="scss">
// Ligne de total de l'historique des mouvements (pied du b-table, hors portée des styles scopés)
.ref-logs-table tfoot th {
  border-top: 2px solid #e7eaec;
  background: #f7f9fa;
  font-weight: 600;
}

.barcode-wrapper> :nth-child(1) {
  font-size: 22px;
}

// QR-002 : le QR est carré, il tient dans la hauteur de l'étiquette (32mm)
.qrcode-wrapper> :nth-child(1) {
  font-size: 22px;
}

.qrcode-svg svg {
  width: 150px;
  height: auto;
}

// Écran : chaque étiquette est centrée dans sa moitié de ligne et bornée à la
// largeur de l'étiquette physique (61mm), pour que les deux restent comparables.
.codes-row {
  .barcode-wrapper,
  .qrcode-wrapper {
    max-width: 61mm;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Reference/ReferenceDetail.vue